import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { database, auth } from "../firebaseconfig"; // Adjust path as needed
import { ref, onValue, off, update, query, orderByChild, startAt, onChildAdded } from "firebase/database";
import { signInAnonymously } from "firebase/auth";
import { getProfile, getVehicleFleetForDriver } from "../../src/ApiController/ApiService"; 

const msgAudioPlayer = new Audio("https://res.cloudinary.com/dncifl1ql/video/upload/v1781458593/ringtone_call_eovric.mp3");

const CallContext = createContext();

// Helper functions for Room IDs
const getConsistentRoomId = (fromId, toId) => {
  const sortedIds = [fromId, toId].sort();
  return `${sortedIds[0]}_${sortedIds[1]}`;
};

const getAllPossibleRoomIds = (fromId, toId) => {
  const sortedRoomId = getConsistentRoomId(fromId, toId);
  return [sortedRoomId, sortedRoomId.split('_').reverse().join('_')];
};

export const CallProvider = ({ children }) => {
  const [incomingCall, setIncomingCall] = useState(null);
  const [globalUser, setGlobalUser] = useState(null);
  const [globalTeamMembers, setGlobalTeamMembers] = useState([]);
  const [isGlobalLoading, setIsGlobalLoading] = useState(true);
  const [isInCall, setIsInCall] = useState(false);

  const acceptHandlerRef = useRef(null);
  const rejectHandlerRef = useRef(null);
  const outgoingHandlerRef = useRef(null);

  const registerCallHandlers = (acceptFn, rejectFn, outgoingFn) => {
    acceptHandlerRef.current = acceptFn;
    rejectHandlerRef.current = rejectFn;
    outgoingHandlerRef.current = outgoingFn;
  };

  // 1. Authenticate & Fetch Data Globally on App Load
  useEffect(() => {
    let isMounted = true;
    
    async function initGlobalData() {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setIsGlobalLoading(false);
        return;
      }
      try {
        // Step A: Firebase Auth (Safe execution)
        try {
          await signInAnonymously(auth);
          await new Promise(res => setTimeout(res, 500)); 
        } catch (authError) {
          console.warn("⚠️ Firebase Auth Info (Might already be logged in):", authError.message);
        }

        // Step B: Fetch Profile
        const profile = await getProfile();
        if (!profile || !profile._id) {
          throw new Error("Profile is missing or API failed to return user data.");
        }
        
        if (isMounted) setGlobalUser(profile);

        // Step C: Fetch Team Members
        let allUsers = [];
        let userMap = new Map();
        
        // Wrap first page fetch in try-catch to avoid crashes
        let firstResponse = null;
        try {
            firstResponse = await getVehicleFleetForDriver(1);
        } catch (apiErr) {
            console.error("⚠️ Failed to fetch first page of team members:", apiErr);
        }
        
        if (firstResponse && firstResponse.docs) {
          allUsers = [...firstResponse.docs];
          let totalPages = firstResponse.totalPages || 1;
          
          if (totalPages > 1) {
            const requests = [];
            for (let p = 2; p <= totalPages; p++) {
              requests.push(
                  getVehicleFleetForDriver(p).catch(err => {
                      console.error(`⚠️ Failed to fetch team page ${p}:`, err);
                      return { docs: [] };
                  })
              );
            }
            const responses = await Promise.all(requests);
            responses.forEach((res) => { 
                if (res && res.docs) {
                    allUsers = [...allUsers, ...res.docs]; 
                }
            });
          }
        }

        allUsers.forEach((u) => { 
            if (u && u._id && !userMap.has(u._id)) {
                userMap.set(u._id, u); 
            }
        });
        
        const uniqueUsers = Array.from(userMap.values());
        
        if (isMounted) {
          setGlobalTeamMembers(uniqueUsers);
          setIsGlobalLoading(false);
          console.log("🟦 CallContext Ready. Team Members Loaded:", uniqueUsers.length);
        }
      } catch (error) {
        console.error("❌ Global Data Fetch Error:", error.message);
        // Fallback state so the app keeps running even if API fails
        if (isMounted) {
            setGlobalTeamMembers([]);
            setIsGlobalLoading(false);
        }
      }
    }
    
    initGlobalData();
    return () => { isMounted = false; };
  }, []);

  // 2. Global Firebase Call Listener (Runs everywhere!)
  useEffect(() => {
    if (!globalUser || !globalUser._id || globalTeamMembers.length === 0) return;

    const userId = globalUser._id; // 🟦 Use Backend ID instead of Firebase UID
    const currentTime = Date.now();
    let lastEndedTime = 0;
    const debounceTime = 2000;
    const callListeners = [];
    const msgListeners = [];

    globalTeamMembers.forEach((member) => {
      const peerId = member._id;
      if (peerId === userId) return; 

      const possibleRoomIds = getAllPossibleRoomIds(userId, peerId);
      possibleRoomIds.forEach((chatRoomId) => {
        const callRef = ref(database, `calls/${chatRoomId}`);
        const unsubscribe = onValue(callRef, (snapshot) => {
          const callData = snapshot.val();
          
          if (callData && callData.status === "ringing" && callData.to === userId && callData.from !== userId) {
            const caller = globalTeamMembers.find((u) => u._id === callData.from);
            
            // Set for the UI Popup
            setIncomingCall({
              callId: chatRoomId,
              status: "ringing",
              from: callData.from,
              fromName: caller?.fullName || "Unknown",
              type: callData.type,
              channelName: callData.channelName,
              token: callData.token,
            });

            // Trigger Browser Notification
            if ("Notification" in window && Notification.permission === "granted") {
              const n = new Notification(`Incoming ${callData.type} Call 📞`, {
                body: `${caller?.fullName || "Someone"} is calling you...`,
                tag: "incoming-call",
                requireInteraction: true // Keeps the notification open until user clicks
              });
              
              // 🟦 FIX: Handle click properly with fresh data
              n.onclick = async (event) => { 
                  event.preventDefault();
                  window.focus(); 
                  n.close(); 
                  
                  // Create fresh call data object right when they click
                  const freshCallData = {
                    callId: chatRoomId,
                    status: "ringing",
                    from: callData.from,
                    fromName: caller?.fullName || "Unknown",
                    type: callData.type,
                    channelName: callData.channelName,
                    token: callData.token,
                  };

                 if (acceptHandlerRef.current) {
  await acceptHandlerRef.current(freshCallData);
} else {
  console.warn("⚠️ Call handler not ready yet.");
}
              };
            }

          } else if (callData && (callData.status === "ended" || callData.status === "rejected") && Date.now() - lastEndedTime > debounceTime) {
            lastEndedTime = Date.now();
            setIncomingCall(null);
          }
        });
        callListeners.push({ path: `calls/${chatRoomId}`, unsubscribe });
        
        // 🟦 Message Listener for Sound Notification
        const msgRef = query(ref(database, `messages/${chatRoomId}`), orderByChild('timestamp'), startAt(currentTime));
        const unsubscribeMsg = onChildAdded(msgRef, (snapshot) => {
          const msg = snapshot.val();
          if (msg && msg.to === userId && msg.from !== userId && msg.timestamp > currentTime) {
            msgAudioPlayer.currentTime = 0;
            const playPromise = msgAudioPlayer.play();
            if (playPromise !== undefined) {
                playPromise.catch(e => console.log("Message audio blocked:", e));
            }
            setTimeout(() => {
                msgAudioPlayer.pause();
                msgAudioPlayer.currentTime = 0;
            }, 1500); // Play short sound for message
          }
        });
        msgListeners.push({ path: `messages/${chatRoomId}`, unsubscribeMsg });
      });
    });

    return () => {
      callListeners.forEach(({ path, unsubscribe }) => {
        off(ref(database, path));
        unsubscribe();
      });
      msgListeners.forEach(({ path, unsubscribeMsg }) => {
        off(ref(database, path));
        unsubscribeMsg();
      });
    };
  }, [globalUser, globalTeamMembers]);

  // Request notification permission manually via interaction, not on mount.

  // 3. Fallback handlers for the UI Popup (IncomingCallPopup.js)
const acceptCall = async () => {
  if (!incomingCall) return;

  if (acceptHandlerRef.current) {
    await acceptHandlerRef.current(incomingCall);
  } else {
    console.warn("⚠️ No call handler registered yet.");
  }
};

  const rejectCall = async () => {
    if (rejectHandlerRef.current && incomingCall) {
      await rejectHandlerRef.current(incomingCall);
    } else if (incomingCall) {
      const callRef = ref(database, `calls/${incomingCall.callId}`);
      try {
        await update(callRef, { status: "rejected", timestamp: Date.now() });
        setIncomingCall(null);
      } catch (error) { 
          console.error("Error rejecting:", error); 
      }
    }
  };

  const startOutgoingCall = async (peer, enableVideo) => {
    if (outgoingHandlerRef.current) {
      await outgoingHandlerRef.current(peer, enableVideo);
    } else {
      console.warn("⚠️ Outgoing call handler not registered.");
    }
  };

  const leaveGlobalCall = async () => {
    if (rejectHandlerRef.current) {
      await rejectHandlerRef.current(incomingCall);
    } else {
      await rejectCall(); // Fallback to basic rejection
    }
  };

  return (
    <CallContext.Provider
      value={{
        globalUser,          
        globalTeamMembers,    
        isGlobalLoading,      
        incomingCall,
        setIncomingCall,
        registerCallHandlers,
        acceptCall,
        rejectCall,
        startOutgoingCall, // 🟦 Exposed
        leaveGlobalCall,    // 🟦 Exposed
        isInGlobalCall: isInCall || !!incomingCall, // 🟦 Updated
        setIsInCall        // 🟦 Exposed setter for GlobalCallManager
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export const useCall = () => useContext(CallContext);